import { useMissingCompanyLegalInfoFields } from '@melio/ap-domain';
import { useMelioForm } from '@melio/penny';
import { Payment, useDeliveryMethods, useFundingSources, useVendors } from '@melio/platform-api';
import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AddBillV2Activity } from '../../add-bill/AddBillV2/AddBillV2.activity';
import { CompleteLegalInfoActivity } from '../../business-legal-info';
import { DeliveryMethodSelectionActivity } from '../../delivery-methods';
import { EditBillActivity } from '../../edit-bill';
import { CreateFundingSourceActivity } from '../../funding-sources';
import { NewSinglePaymentStepLayout } from '../../NewSinglePaymentStepLayout';
import { PaymentFlowFormFields } from '../types';
import { PaymentFlowAction, PaymentFlowForm } from './PaymentFlowForm/PaymentFlowForm';
import { usePaymentFlowFormSchema } from './PaymentFlowForm/usePaymentFlowSchema';
import { useFormChangeHandlers } from './useFormChangeHandlers';
import { usePaymentFlowBillDetails } from './usePaymentFlowBillDetails';
import { usePaymentFlowDefaultValues } from './usePaymentFlowDefaultValues';
import { usePaymentFlowNavigation } from './usePaymentFlowNavigation';
import { usePaymentSettingsData } from './usePaymentSettingsData';

export type PaymentFlowActivityProps = {
  defaultValues: Partial<PaymentFlowFormFields>;
  billId?: string;
  payment?: Payment;
  paymentFlowAction: PaymentFlowAction;
  isSubmitting: boolean;
  onSubmit: (data: PaymentFlowFormFields) => Promise<unknown>;
  onClose: VoidFunction;
};

export const PaymentFlowActivity = ({
  defaultValues,
  billId,
  payment,
  paymentFlowAction,
  isSubmitting,
  onSubmit,
  onClose,
}: PaymentFlowActivityProps) => {
  const navigate = usePaymentFlowNavigation();
  const { missingCompanyLegalInfoFields, isLoading: isMissingLegalInfoFieldsLoading } =
    useMissingCompanyLegalInfoFields() || {};

  const {
    bill,
    file,
    isLoading: isBillDetailsLoading,
  } = usePaymentFlowBillDetails({
    billId,
  });

  const form = useMelioForm<PaymentFlowFormFields>({
    onSubmit: (data) => {
      if (missingCompanyLegalInfoFields && missingCompanyLegalInfoFields.length > 0) {
        return navigate.toLegal();
      } else {
        return onSubmit(data);
      }
    },
    defaultValues: usePaymentFlowDefaultValues(defaultValues),
    schema: usePaymentFlowFormSchema(bill),
  });

  const { data: vendors = [], isLoading: isVendorsLoading } = useVendors();
  const { data: fundingSources = [], isLoading: isFundingSourcesLoading } = useFundingSources();
  const [vendorId, fundingSourceId, amountToPay] = form.watch(['vendorId', 'fundingSourceId', 'amountToPay']);
  const { data: deliveryMethods = [], isLoading: isDeliveryMethodsLoading } = useDeliveryMethods({
    vendorId,
  });

  const {
    paymentSettings,
    fetchPaymentSettings,
    isLoading: isPaymentSettingsLoading,
  } = usePaymentSettingsData({
    form,
    defaultValues,
  });

  const {
    onVendorChange,
    onDeliveryMethodChange,
    onAmountChange,
    onFundingSourceChange,
    onDeliveryDateChange,
    onBillChange,
    onRecurrenceChange,
  } = useFormChangeHandlers({
    form,
    paymentSettings,
    onChangeCallback: fetchPaymentSettings,
    vendors,
  });

  if (isBillDetailsLoading || isVendorsLoading || isSubmitting || isMissingLegalInfoFieldsLoading) {
    return <NewSinglePaymentStepLayout isLoading />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <PaymentFlowForm
            form={form}
            paymentSettings={paymentSettings}
            onVendorChange={onVendorChange}
            onAmountChange={onAmountChange}
            onFundingSourceChange={onFundingSourceChange}
            onNewFundingSourceClick={navigate.toFundingSource}
            onDeliveryDateChange={onDeliveryDateChange}
            onDeliveryMethodClick={navigate.toDeliveryMethod}
            onBillButtonClick={() => navigate.toBill(bill)}
            onRecurrenceChange={onRecurrenceChange}
            fundingSources={fundingSources}
            deliveryMethods={deliveryMethods}
            vendors={vendors}
            payment={payment}
            bill={bill}
            file={file}
            isLoading={isPaymentSettingsLoading || isDeliveryMethodsLoading || isFundingSourcesLoading}
            paymentFlowAction={paymentFlowAction}
            onClose={onClose}
          />
        }
      />
      <Route
        path="/delivery-method"
        element={
          vendorId ? (
            <DeliveryMethodSelectionActivity
              vendorId={vendorId}
              fundingSourceId={fundingSourceId}
              deliveryMethodTypeOptions={paymentSettings?.deliveryMethodTypeOptions}
              onBack={navigate.toRoot}
              onClose={navigate.toRoot}
              onDone={(deliveryMethod) => {
                onDeliveryMethodChange(deliveryMethod.id);
                navigate.toRoot();
              }}
            />
          ) : (
            <Navigate to=".." />
          )
        }
      />
      <Route
        path="/funding-source"
        element={
          <CreateFundingSourceActivity
            fundingSourceTypesOptions={paymentSettings?.fundingSourceTypesOptions}
            onClose={navigate.toRoot}
            onDone={(fundingSource) => {
              onFundingSourceChange(fundingSource.id);
              navigate.toRoot();
            }}
          />
        }
      />
      <Route
        path="/bill/add"
        element={
          <AddBillV2Activity
            initialValues={{
              vendorId,
              amount: amountToPay,
            }}
            onDone={(data, billId, saveType) => {
              if (billId) {
                if (saveType === 'close') {
                  return onClose();
                }
                onBillChange({ vendorId: data.vendorId, amount: data.billAmount });
                navigate.toRoot({ search: new URLSearchParams({ billId }) });
              }
            }}
            onClose={navigate.toRoot}
            onBack={navigate.toRoot}
          />
        }
      />
      <Route
        path="/bill/edit"
        element={
          bill ? (
            <EditBillActivity
              id={bill.id}
              onClose={navigate.toRoot}
              onBack={navigate.toRoot}
              onDone={(data, saveType) => {
                if (saveType === 'close') {
                  return onClose();
                }
                onBillChange({ vendorId: data.vendorId, amount: data.amount });
                navigate.toRoot();
              }}
            />
          ) : (
            <Navigate to=".." />
          )
        }
      />
      <Route
        path="/legal"
        element={
          <CompleteLegalInfoActivity
            onBack={navigate.toRoot}
            onClose={navigate.toRoot}
            onDone={() => onSubmit(form.getValues())}
          />
        }
      />

      <Route path="*" element={<Navigate to=".." />} />
    </Routes>
  );
};
